// Home / Today (web) — Phase 1 render. Consumes the gateway BFF GET /api/v1/home/today and shows
// ranked HomeSignal[] grouped Needs-attention / Coming-up / Snapshot / Engagement. Priority is
// server-computed and re-applied client-side (identical deterministic rules) after local dismissals
// and expiry. Localizes by signal `type` with a graceful fallback to the BFF English `title`.
// Empty-state disciplined: empty groups hide; a fully clear day shows one positive line. Degrades:
// an unavailable source contributes no signal and surfaces a subtle "may be incomplete" note.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Loader2, AlertCircle, Clock, Info, Sparkles, ChevronRight, X, Lock, CheckCircle2,
  CalendarPlus, UserCog, FileText, Wallet,
} from 'lucide-react';
import { buildTenantPath } from '@/features/navigation/utils';
import { openDana } from '@/lib/dana';
import { getHomeToday } from './homeApi';
import { rankSignals, type HomeSignal, type HomeSource } from './homeSignal';

const DISMISS_KEY = 'kp.home.dismissed';

const loadDismissed = (): Set<string> => {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]')); } catch { return new Set(); }
};
const saveDismissed = (s: Set<string>) => {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...s])); } catch { /* ignore */ }
};

// Signal → localized label: try home:signal.<type_with_underscores> (with count + plural), else the
// BFF's English fallback title. This is the recommended localization path (contract §6 option A).
function useSignalText() {
  const { t } = useTranslation(['home']);
  return (s: HomeSignal): string => {
    const key = `home:signal.${s.type.replace(/\./g, '_')}`;
    return t(key, { count: s.count ?? 1, defaultValue: s.title });
  };
}

const greetingKey = (): 'morning' | 'afternoon' | 'evening' => {
  const h = new Date().getHours();
  return h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
};

type Group = 'attention' | 'upcoming' | 'snapshot' | 'engagement';
const groupOf = (s: HomeSignal, now: number): Group => {
  const overdue = s.dueAt ? Date.parse(s.dueAt) < now : false;
  if (s.priority === 'CRITICAL' || s.priority === 'ACTION_REQUIRED' || (s.priority === 'DUE_SOON' && overdue)) return 'attention';
  if (s.priority === 'DUE_SOON') return 'upcoming';
  if (s.priority === 'ENGAGEMENT') return 'engagement';
  return 'snapshot';
};

const iconFor = (s: HomeSignal) => {
  if (s.priority === 'CRITICAL' || s.priority === 'ACTION_REQUIRED') return AlertCircle;
  if (s.priority === 'DUE_SOON') return Clock;
  if (s.priority === 'ENGAGEMENT') return Sparkles;
  return Info;
};

export const HomeToday: React.FC = () => {
  const { t } = useTranslation(['home']);
  const text = useSignalText();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const firstName = useSelector((st: any) => st?.auth?.user?.first_name || st?.auth?.user?.firstName || '');

  const [signals, setSignals] = useState<HomeSignal[]>([]);
  const [sources, setSources] = useState<HomeSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(loadDismissed);

  const load = () => {
    setLoading(true); setError(false);
    getHomeToday()
      .then((r) => { setSignals(r.signals || []); setSources(r.sources || []); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const dismiss = (id: string) => {
    const next = new Set(dismissed); next.add(id); setDismissed(next); saveDismissed(next);
  };

  // Re-rank client-side (identical rules) so local dismissals + expiry apply after the server rank.
  const visible = useMemo(() => rankSignals(signals, { dismissed, now: Date.now() }), [signals, dismissed]);
  const now = Date.now();
  const groups = useMemo(() => {
    const g: Record<Group, HomeSignal[]> = { attention: [], upcoming: [], snapshot: [], engagement: [] };
    for (const s of visible) g[groupOf(s, now)].push(s);
    return g;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const degraded = sources.some((s) => s.status === 'unavailable');

  const go = (s: HomeSignal) => {
    if (!s.action) return;
    if (s.action.external) { window.open(s.action.href, '_blank', 'noopener'); return; }
    navigate(buildTenantPath(s.action.href.replace(/^\/app\/?/, ''), tenantSlug));
  };

  // Per-group visual treatment — brand-warm surfaces + severity, not flat gray boxes.
  const STYLE: Record<Group, { card: string; chip: string; accent: string; filled: boolean }> = {
    attention: { card: 'border-red-200/70 bg-red-50/40 dark:border-red-500/25 dark:bg-red-500/[0.06]', chip: 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400', accent: 'text-red-600', filled: true },
    upcoming: { card: 'border-amber-200/70 bg-amber-50/40 dark:border-amber-500/25 dark:bg-amber-500/[0.06]', chip: 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400', accent: 'text-amber-600', filled: true },
    engagement: { card: 'border-primary-200/70 bg-primary-50/40 dark:border-primary-500/25 dark:bg-primary-500/[0.06]', chip: 'bg-primary-100 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400', accent: 'text-primary-600', filled: false },
    snapshot: { card: 'border-border bg-card', chip: 'bg-muted text-muted-foreground', accent: 'text-charcoal', filled: false },
  };

  const Row = (s: HomeSignal) => {
    const Icon = iconFor(s);
    const g = groupOf(s, now);
    const st = STYLE[g];
    return (
      <div key={s.id} className={`group flex items-start gap-3.5 rounded-2xl border p-4 shadow-sm transition-shadow hover:shadow-md ${st.card}`}>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${st.chip}`}><Icon className="h-[18px] w-[18px]" /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-[15px] font-semibold leading-snug text-charcoal">{text(s)}</p>
            {s.sensitive && <Lock className="h-3 w-3 shrink-0 text-muted-foreground" aria-label={t('home:sensitive')} />}
          </div>
          {s.summary && <p className="mt-0.5 text-[13px] text-muted-foreground">{s.summary}</p>}
          {s.action && (
            st.filled ? (
              <button type="button" onClick={() => go(s)}
                className="mt-2.5 inline-flex items-center gap-1 rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-600">
                {s.action.label} <ChevronRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button type="button" onClick={() => go(s)}
                className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700">
                {s.action.label} <ChevronRight className="h-3.5 w-3.5" />
              </button>
            )
          )}
        </div>
        {s.dismissible && (
          <button type="button" aria-label={t('home:dismiss')} onClick={() => dismiss(s.id)}
            className="shrink-0 rounded-lg p-1 text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  const Section = (key: Group, label: React.ReactNode) => {
    const items = groups[key];
    if (items.length === 0) return null;
    return (
      <section>
        <div className="mb-2.5 flex items-center gap-2">
          <h2 className="text-sm font-bold text-charcoal">{t(`home:group.${key}`)}</h2>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">{items.length}</span>
          {label}
        </div>
        <div className="flex flex-col gap-2.5">{items.map(Row)}</div>
      </section>
    );
  };

  // Quick actions — always present utility (assessment IA §J).
  const quickActions: { key: string; icon: React.ComponentType<any>; href: string }[] = [
    { key: 'request_leave', icon: CalendarPlus, href: 'time-off' },
    { key: 'update_profile', icon: UserCog, href: 'personal' },
    { key: 'documents', icon: FileText, href: 'documents' },
    { key: 'my_pay', icon: Wallet, href: 'my-pay' },
  ];

  const greeting = `${t(`home:greeting.${greetingKey()}`)}${firstName ? `, ${firstName}` : ''}`;
  const dateStr = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="mx-auto max-w-6xl p-6 lg:p-8">
      <header className="mb-7">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary-500">{dateStr}</p>
        <h1 className="mt-1 text-[28px] font-extrabold leading-tight text-charcoal">{greeting} <span className="inline-block">👋</span></h1>
        <p className="mt-0.5 text-sm text-muted-foreground">{t('home:subtitle')}</p>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('home:loading')}</div>
      ) : error ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('home:error')}</p>
          <button type="button" onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600">{t('home:retry')}</button>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main column — the signals */}
          <div className="space-y-6 lg:col-span-2">
            {degraded && (
              <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-3.5 py-2.5 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                <Info className="h-3.5 w-3.5" /> {t('home:sources_degraded')}
              </div>
            )}
            {visible.length === 0 ? (
              <div className="rounded-2xl border border-border bg-gradient-to-br from-green-50/60 to-card p-10 text-center dark:from-green-500/[0.06]">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-green-100 dark:bg-green-500/15"><CheckCircle2 className="h-6 w-6 text-green-600" /></div>
                <p className="text-lg font-bold text-charcoal">{t('home:empty.all_clear')}</p>
                <p className="mt-1 text-sm text-muted-foreground">{t('home:empty.all_clear_sub')}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Section('attention', null)}
                {Section('upcoming', null)}
                {Section('snapshot', null)}
                {Section('engagement', null)}
              </div>
            )}
          </div>

          {/* Right rail — Dana + Quick actions */}
          <aside className="space-y-5">
            {/* Dana Daily Brief — narrates the SAME BFF signals (get_my_day), never recomputes. */}
            <div className="rounded-2xl border border-primary-200/70 bg-gradient-to-br from-primary-50 to-orange-50 p-5 dark:border-primary-500/25 dark:from-primary-500/[0.08] dark:to-orange-500/[0.05]">
              <div className="mb-1 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary-500 text-white"><Sparkles className="h-4 w-4" /></div>
                <span className="text-sm font-bold text-charcoal">Dana</span>
              </div>
              <p className="text-[13px] text-muted-foreground">{t('home:dana_brief')}</p>
              <button type="button" onClick={() => openDana('Give me a brief for today — what needs my attention?')}
                className="mt-3 w-full rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-600">
                {t('home:dana_brief')}
              </button>
            </div>

            <div className="rounded-2xl border border-border bg-card p-5">
              <h2 className="mb-3 text-sm font-bold text-charcoal">{t('home:quick_actions')}</h2>
              <div className="grid grid-cols-2 gap-2.5">
                {quickActions.map((a) => {
                  const Icon = a.icon;
                  return (
                    <button key={a.key} type="button" onClick={() => navigate(buildTenantPath(a.href, tenantSlug))}
                      className="flex flex-col items-center gap-2 rounded-xl border border-border bg-background p-3.5 text-center transition hover:border-primary-300 hover:bg-primary-50/40 dark:hover:bg-primary-500/[0.06]">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-500/15"><Icon className="h-[18px] w-[18px]" /></span>
                      <span className="text-xs font-medium text-charcoal">{t(`home:qa.${a.key}`)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default HomeToday;
